import {parsePrivateMediaAuthority} from '@ohmyprime/p2p-shared';
import {creatorClient} from './creator-client';
import {selectedClient} from './remote/api';
import type { GamingControlProvider, GamingSession } from '../net/api';
const id = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= 128;
export function isGamingSession(value: unknown): value is GamingSession {
  if (!value || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  if (s.closeRequest !== undefined) {
    const c=s.closeRequest as Record<string,unknown>;
    if (!c || typeof c !== 'object' || Object.keys(c).sort().join(',') !== 'deadlineMs,requestId,requestedReason' || !id(c.requestId) || !Number.isSafeInteger(c.deadlineMs) || Number(c.deadlineMs)<0 || c.requestedReason!=='owner_end') return false;
  }
  if (s.endReason !== undefined && !id(s.endReason)) return false;
  if (s.tickStatus !== undefined && !['confirmed','unavailable'].includes(String(s.tickStatus))) return false;
  if (s.finalTick !== undefined && s.finalTick !== null && (!Number.isSafeInteger(s.finalTick) || Number(s.finalTick)<0)) return false;
  if (s.endingDeadlineMs !== undefined && (!Number.isSafeInteger(s.endingDeadlineMs) || Number(s.endingDeadlineMs)<0)) return false;
  return ['sessionId', 'streamId', 'gameId', 'versionId', 'backendId', 'transitionId'].every(k => id(s[k])) &&
    ['epoch', 'revision'].every(k => Number.isSafeInteger(s[k]) && Number(s[k]) > 0) &&
    ['PREPARING', 'ACTIVE', 'ENDING', 'ENDED'].includes(String(s.state)) && typeof s.allowBackendEnd === 'boolean';
}
/** Only the exact authenticated portal parent can answer. No token is requested or accepted. */
export function createGamingClient(options: { parentOrigin: string; host?: Window; timeoutMs?: number }): GamingControlProvider {
  const host = options.host ?? window;
  const origin = new URL(options.parentOrigin).origin;
  if (host.parent === host || origin === 'null') throw new Error('Gaming requires the portal frame.');
  const nonce = crypto.randomUUID();
  const sessionListeners=new Set<(hint:{sessionId:string;streamId:string;epoch:number;revision:number})=>void>();
  let bridgeEpoch:string|undefined;
  const lifecycle=(event:MessageEvent)=>{
    const d=event.data;if(event.origin!==origin||event.source!==host.parent||!d||d.nonce!==nonce)return;
    if(d.type==='omp:gaming-ready'&&id(d.epoch)){bridgeEpoch=d.epoch;return;}
    if(d.type!=='omp:gaming-lifecycle'||d.epoch!==bridgeEpoch||!id(d.sessionId)||!id(d.streamId)||!Number.isSafeInteger(d.sessionEpoch)||d.sessionEpoch<1||!Number.isSafeInteger(d.revision)||d.revision<1)return;
    for(const listener of sessionListeners)listener({sessionId:d.sessionId,streamId:d.streamId,epoch:d.sessionEpoch,revision:d.revision});
  };
  let producerBusy=false,cancelProducer:(()=>void)|undefined;
  const client: GamingControlProvider = {
    selected:selectedClient(host,origin,nonce),
    creatorRender:creatorClient(host,origin,nonce),
    subscribeSession(listener){if(!sessionListeners.size)host.addEventListener('message',lifecycle);sessionListeners.add(listener);return()=>{sessionListeners.delete(listener);if(!sessionListeners.size)host.removeEventListener('message',lifecycle);};},
    runtimeMedia(operation,identifier,streamId,body,context){
      if(producerBusy)return Promise.reject(Error('Runtime producer busy'));
      producerBusy=true;
      const requestId=crypto.randomUUID(),snapshot=structuredClone(body);
      return new Promise((resolve,reject)=>{
        let epoch:string|null=null,settled=false;
        const finish=(error?:Error,value?:Awaited<ReturnType<NonNullable<GamingControlProvider['runtimeMedia']>>>)=>{
          if(settled)return;settled=true;clearTimeout(timer);host.removeEventListener('message',receive);context.signal.removeEventListener('abort',cancel);
          if(cancelProducer===cancel)cancelProducer=undefined;producerBusy=false;
          if(error)reject(error);else resolve(value!);
        };
        const valid=()=>!context.signal.aborted&&Date.now()<context.deadlineMs&&context.current();
        const cancel=()=>finish(Error('Runtime producer cancelled'));
        const receive=(event:MessageEvent)=>{
          const d=event.data;if(event.origin!==origin||event.source!==host.parent||!d||d.nonce!==nonce)return;
          if(!valid()){cancel();return;}
          if(d.type==='omp:gaming-ready'&&id(d.epoch)&&!epoch){epoch=d.epoch;host.parent.postMessage({type:'omp:gaming-request',nonce,epoch,requestId,operation,id:identifier,streamId,body:snapshot,deadlineMs:context.deadlineMs},origin);}
          else if(d.type==='omp:gaming-result'&&d.epoch===epoch&&d.requestId===requestId){
            const r=d.runtimeMedia;
            if(d.error||!r||!Number.isFinite(r.expiresAtMs)){finish(Object.assign(Error('Runtime media unavailable'),{code:d.code,retryAfterMs:d.retryAfterMs}));return;}
            if(operation==='recordRuntimeMediaEvidence'){
              if(!id(r.evidenceId)||r.program!=='verified'||!['verified','absent'].includes(r.camera)||!['verified','absent'].includes(r.microphone)){finish(Error('Invalid runtime receipt'));return;}
              finish(undefined,{evidenceId:r.evidenceId,expiresAtMs:r.expiresAtMs,program:'verified',camera:r.camera,microphone:r.microphone});
            }else if(!id(r.challengeId)){finish(Error('Invalid runtime challenge'));}
            else if(operation==='ackRuntimeMediaChallenge'){
              if(!('sourceMonotonicMs'in snapshot)||r.challengeId!==snapshot.challengeId||r.sourceMonotonicMs!==snapshot.sourceMonotonicMs||![r.serverLowerMs,r.serverUpperMs].every(Number.isFinite)||r.serverUpperMs<r.serverLowerMs||r.serverUpperMs-r.serverLowerMs>200){finish(Error('Invalid runtime mapping'));return;}
              finish(undefined,{challengeId:r.challengeId,expiresAtMs:r.expiresAtMs,sourceMonotonicMs:r.sourceMonotonicMs,serverLowerMs:r.serverLowerMs,serverUpperMs:r.serverUpperMs});
            }else finish(undefined,{challengeId:r.challengeId,expiresAtMs:r.expiresAtMs});
          }
        };
        const timer=setTimeout(cancel,Math.max(0,Math.min(5000,context.deadlineMs-Date.now())));
        cancelProducer=cancel;context.signal.addEventListener('abort',cancel,{once:true});
        if(!valid()){cancel();return;}
        host.addEventListener('message',receive);host.parent.postMessage({type:'omp:gaming-hello',nonce},origin);
      });
    },
    recordMarker(identifier, streamId, body, context) {
      const requestId = crypto.randomUUID();
      const {epoch:sessionEpoch, revision, receiptId} = body;
      return new Promise((resolve,reject) => {
        let epoch: string | null = null;
        const finish = () => { clearTimeout(timer); host.removeEventListener('message', receive); };
        const valid = () => { try { return context.current() && Date.now() < context.deadlineMs; } catch { return false; } };
        const receive = (event: MessageEvent) => {
          const d = event.data;
          if (event.origin !== origin || event.source !== host.parent || !d || d.nonce !== nonce) return;
          if (!valid()) { finish(); reject(Error('Marker source changed')); return; }
          if (d.type === 'omp:gaming-ready' && id(d.epoch) && !epoch) {
            epoch = d.epoch;
            host.parent.postMessage({type:'omp:gaming-request',nonce,epoch,requestId,operation:'recordReturnMarker',id:identifier,streamId,
              operationId:context.operationId,deadlineMs:context.deadlineMs,body:{epoch:sessionEpoch,revision,receiptId}},origin);
          } else if (d.type === 'omp:gaming-result' && d.epoch === epoch && d.requestId === requestId) {
            finish(); const r = d.observation;
            if (d.error || !r || r.sessionId !== identifier || r.epoch !== sessionEpoch || r.revision !== revision + 1 ||
                r.receiptId !== receiptId || !Number.isFinite(r.issuedAtMs) || !Number.isFinite(r.expiresAtMs) ||
                r.expiresAtMs <= Date.now() || r.expiresAtMs > context.deadlineMs || !r.marker || r.marker.streamId !== streamId || r.marker.mediaGeneration !== r.mediaGeneration ||
                !['captureTimeMs','mediaTimeMs','uncertaintyMs','finalTick'].every(k => Number.isSafeInteger(r.marker[k]) && r.marker[k] >= 0) || r.marker.uncertaintyMs > 100) {
              reject(Error('Marker observation unavailable')); return;
            }
            resolve(r);
          }
        };
        // Keep this RPC listener/queue slot until the shared attempt deadline. An earlier
        // timeout would abandon a healthy portal write and make its exact retry BUSY.
        const timer = setTimeout(() => {finish();reject(Error('Marker request timed out'));},Math.max(0,context.deadlineMs-Date.now()));
        if (!valid()) {finish();reject(Error('Marker source changed'));return;}
        host.addEventListener('message',receive);host.parent.postMessage({type:'omp:gaming-hello',nonce},origin);
      });
    },
    publicationReceipt(identifier, streamId, body, current) {
      // Selected receipts require the Task9 owner bridge; never flatten their
      // causal program binding into a legacy locator.
      if ('profile' in body) return Promise.reject(Error('selected_publication_bridge_unavailable'));
      const requestId = crypto.randomUUID();
      // Copy only the locator and local bridge fences before any queued or asynchronous work.
      const {epoch: sessionEpoch, revision, sourceId, broadcastId, keyId, renditionId, seq} = body;
      return new Promise((resolve, reject) => {
        let epoch: string | null = null;
        const finish = () => { clearTimeout(timer); host.removeEventListener('message', receive); };
        const valid = () => { try { return current(); } catch { return false; } };
        const receive = (event: MessageEvent) => {
          const d = event.data;
          if (event.origin !== origin || event.source !== host.parent || !d || d.nonce !== nonce) return;
          if (!valid()) { finish(); reject(Error('Publication source changed')); return; }
          if (d.type === 'omp:gaming-ready' && id(d.epoch) && !epoch) {
            epoch = d.epoch;
            host.parent.postMessage({type:'omp:gaming-request',nonce,epoch,requestId,operation:'issuePublicationReceipt',id:identifier,streamId,
              body:{epoch:sessionEpoch,revision,sourceId,broadcastId,keyId,renditionId,seq}}, origin);
          } else if (d.type === 'omp:gaming-result' && d.epoch === epoch && d.requestId === requestId) {
            finish();
            const r = d.receipt;
            if (d.error || !r || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(r.receiptId) ||
                r.sessionId !== identifier || r.epoch !== sessionEpoch || r.revision !== revision ||
                !Number.isFinite(r.expiresAtMs) || r.expiresAtMs <= Date.now() || r.expiresAtMs > Date.now() + 5000) {
              reject(Error('Publication receipt unavailable')); return;
            }
            const {receiptId,sessionId,epoch:receiptEpoch,revision:receiptRevision,expiresAtMs} = r;
            resolve({receiptId,sessionId,epoch:receiptEpoch,revision:receiptRevision,expiresAtMs});
          }
        };
        const timer = setTimeout(() => { finish(); reject(Error('Publication receipt timed out')); }, options.timeoutMs ?? 5000);
        if (!valid()) { finish(); reject(Error('Publication source changed')); return; }
        host.addEventListener('message', receive);
        host.parent.postMessage({type:'omp:gaming-hello',nonce}, origin);
      });
    },
    sourceChallenge(operation, identifier, streamId, body) {
      const requestId = crypto.randomUUID();
      return new Promise((resolve,reject)=>{
        let epoch:string|null=null;
        const finish=()=>{clearTimeout(timer);host.removeEventListener('message',receive);};
        const receive=(event:MessageEvent)=>{
          const d=event.data;
          if(event.origin!==origin || event.source!==host.parent || !d || d.nonce!==nonce) return;
          if(d.type==='omp:gaming-ready' && id(d.epoch) && !epoch){
            epoch=d.epoch;
            host.parent.postMessage({type:'omp:gaming-request',nonce,epoch,requestId,operation,id:identifier,streamId,body},origin);
          } else if(d.type==='omp:gaming-result' && d.epoch===epoch && d.requestId===requestId){
            finish();const r=d.challenge;
            if(d.error || !r || !id(r.challengeId) || !Number.isFinite(r.expiresAtMs) || r.expiresAtMs<0){reject(Error('Source challenge unavailable'));return;}
            if(operation==='ackSourceChallenge' && (!('challengeId' in body) || r.challengeId!==body.challengeId || r.sourceMonotonicMs!==body.sourceMonotonicMs || !Number.isFinite(r.serverTimeMs) || r.serverTimeMs<0 || !Number.isFinite(r.uncertaintyMs) || r.uncertaintyMs<0 || r.uncertaintyMs>100)){reject(Error('Invalid clock mapping'));return;}
            resolve(r);
          }
        };
        const timer=setTimeout(()=>{finish();reject(Error('Source challenge timed out'));},options.timeoutMs??15000);
        host.addEventListener('message',receive);host.parent.postMessage({type:'omp:gaming-hello',nonce},origin);
      });
    },
    reaction(operation, identifier, streamId, body, context) {
      const requestId = crypto.randomUUID();
      return new Promise((resolve, reject) => {
        let epoch: string | null = null;
        const cancel=()=>{finish();reject(Error('Reaction operation cancelled'));};
        const current=()=>!context || !context.signal.aborted&&context.current()&&Date.now()<context.deadlineMs;
        const finish=()=>{clearTimeout(timer);host.removeEventListener('message',receive);context?.signal.removeEventListener('abort',cancel);};
        const receive=(event:MessageEvent)=>{
          const d=event.data;
          if(event.origin!==origin||event.source!==host.parent||!d||d.nonce!==nonce)return;
          if(!current()){cancel();return;}
          if(d.type==='omp:gaming-ready'&&id(d.epoch)&&!epoch){
            epoch=d.epoch;
            host.parent.postMessage({type:'omp:gaming-request',nonce,epoch,requestId,operation,id:identifier,streamId,body,...(context?{operationId:requestId,deadlineMs:context.deadlineMs}:{})},origin);
          }else if(d.type==='omp:gaming-result'&&d.epoch===epoch&&d.requestId===requestId){
            finish();const r=d.reaction;
            if(d.error||!r){reject(Object.assign(Error('Reaction unavailable'),{code:d.code}));return;}
            const validConsent=(v:{consentEpoch?:number;enabled?:boolean}|undefined)=>!!v&&Number.isSafeInteger(v.consentEpoch)&&v.consentEpoch!>=0&&typeof v.enabled==='boolean';
            if(operation==='getReactionConsent'){
              if(!validConsent(r.camera)||!validConsent(r.microphone)){reject(Error('Invalid reaction consent'));return;}
            }else if(operation==='getReactionMediaCatalog'){
              if(r.sessionId!==identifier||!id(r.publicationId)||!id(r.broadcastId)||!Array.isArray(r.publications)||r.publications.length>2||r.publications.some((a:unknown)=>{const authority=parsePrivateMediaAuthority(a);return !authority||authority.sessionId!==identifier||authority.publicationId!==r.publicationId;})||!Array.isArray(r.mediaStates)||r.mediaStates.length>2||r.mediaStates.some((m:{medium:string;state:string;consentEpoch:number})=>!['camera','microphone'].includes(m.medium)||!['off','awaiting-init','current','revoked'].includes(m.state)||!Number.isSafeInteger(m.consentEpoch)||m.consentEpoch<0)){reject(Error('Invalid reaction catalog'));return;}
            }else if(operation==='issueReactionGrant'||operation==='renewReactionGrant'){
              if(!body||!('publicationId' in body)||r.transport!=='omp-p2p'||r.namespace!=='reaction'||r.sessionId!==identifier||r.publicationId!==body.publicationId||r.medium!==body.medium||r.consentEpoch!==body.consentEpoch||r.role!=='publisher'||!Array.isArray(r.scopes)||!r.scopes.includes('publish')||!id(r.generation)||!id(r.identity)||!id(r.broadcastId)||typeof r.token!=='string'||!r.token||r.token.length>16384||typeof r.trackerUrl!=='string'||!r.trackerUrl.startsWith('wss://')||!Number.isFinite(Date.parse(r.expiresAt))||Date.parse(r.expiresAt)<=Date.now()||Date.parse(r.expiresAt)>Date.now()+30000||operation==='renewReactionGrant'&&(!('identity' in body)||r.identity!==body.identity||r.generation!==body.generation)){reject(Error('Invalid reaction grant'));return;}
            }else if(!validConsent(r)){reject(Error('Invalid reaction consent'));return;}
            resolve(r);
          }
        };
        const timer=setTimeout(()=>{finish();reject(Error('Reaction portal request timed out'));},Math.min(options.timeoutMs??15000,context?Math.max(0,context.deadlineMs-Date.now()):15000));
        if(!current()){cancel();return;}context?.signal.addEventListener('abort',cancel,{once:true});
        host.addEventListener('message',receive);host.parent.postMessage({type:'omp:gaming-hello',nonce},origin);
      });
    },
    request(operation, identifier, streamId, body, context) {
      const requestId = crypto.randomUUID();
      return new Promise((resolve, reject) => {
        let epoch: string | null = null;
        const cancel = () => finish(Error('Gaming operation superseded'));
        const finish = (error?: Error, session?: GamingSession) => {
          clearTimeout(timer); host.removeEventListener('message', receive);
          context?.signal.removeEventListener('abort', cancel);
          if (error) reject(error); else resolve(session!);
        };
        const receive = (event: MessageEvent) => {
          if (event.origin !== origin || event.source !== host.parent || !event.data || event.data.nonce !== nonce) return;
          const d = event.data;
          if (context && (context.signal.aborted || !context.current())) { cancel(); return; }
          if (d.type === 'omp:gaming-ready' && id(d.epoch) && !epoch) {
            epoch = d.epoch;
            host.parent.postMessage({ type: 'omp:gaming-request', nonce, epoch, requestId, operation, id: identifier, streamId, ...(operation === 'recordReturnMarker' ? { body } : {}) }, origin);
          } else if (d.type === 'omp:gaming-result' && d.epoch === epoch && d.requestId === requestId) {
            if (typeof d.error === 'string') finish(Object.assign(new Error('Gaming unavailable. Check the portal login and version readiness.'), { code: d.code }));
            else if (isGamingSession(d.session) && d.session.streamId === streamId) finish(undefined, d.session);
            else finish(new Error('Invalid Gaming session response.'));
          }
        };
        const timer = setTimeout(() => finish(new Error('Gaming portal request timed out.')), options.timeoutMs ?? (operation==='prepareSession'?30_000:15_000));
        if (context?.signal.aborted || context && !context.current()) { cancel(); return; }
        context?.signal.addEventListener('abort', cancel, {once: true});
        host.addEventListener('message', receive);
        host.parent.postMessage({ type: 'omp:gaming-hello', nonce }, origin);
      });
    },
  };
  // Control polling and reaction consent/grants share one portal RPC lane.
  // Begin the timeout only when dispatched, never while waiting behind a healthy request.
  const queue: (() => void)[] = [];
  let running = false, laneGeneration = 0;
  const pump = () => {
    if (running) return;
    const next = queue.shift();
    if (next) { running = true; next(); }
  };
  const enqueue = <T>(work: () => Promise<T>): Promise<T> => new Promise((resolve, reject) => {
    if (queue.length >= 16) { reject(Object.assign(Error('Gaming bridge queue full'), { code: 'GAMING_BUSY' })); return; }
    const generation = laneGeneration;
    queue.push(() => {
      const run = () => generation === laneGeneration ? work() : Promise.reject(Error('Gaming operation superseded'));
      void run().then(resolve, reject).finally(() => { running = false; pump(); });
    });
    pump();
  });
  return {
    subscribeSession:listener=>client.subscribeSession!(listener),
    creatorRender:(...args)=>client.creatorRender!(...args),
    runtimeMedia:(...args)=>client.runtimeMedia!(...args),
    selected:client.selected,
    recordMarker: (identifier, streamId, body, context) => {
      const snapshot = {...body}, fence = {...context};
      return enqueue(() => client.recordMarker!(identifier, streamId, snapshot, fence));
    },
    publicationReceipt: (identifier, streamId, body, current) => {
      const snapshot = {...body};
      return enqueue(() => client.publicationReceipt!(identifier, streamId, snapshot, current));
    },
    sourceChallenge: (...args) => enqueue(() => client.sourceChallenge!(...args)),
    request: (...args) => {
      if (args[0] !== 'getSession') cancelProducer?.();
      if (args[0] === 'endSession') { laneGeneration++; return client.request(...args); }
      return enqueue(() => client.request(...args));
    },
    reaction: (...args) => {
      const context=args[4];
      if(!context)return enqueue(()=>client.reaction!(...args));
      return new Promise((resolve,reject)=>{
        const cancel=()=>reject(Error('Reaction operation cancelled'));
        if(context.signal.aborted||!context.current()){cancel();return;}
        context.signal.addEventListener('abort',cancel,{once:true});
        void enqueue(()=>client.reaction!(...args)).then(resolve,reject).finally(()=>context.signal.removeEventListener('abort',cancel));
      });
    },
  };
}
